import { Component, EventEmitter, inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { BaseModal } from '../base/base-modal';
import { ErrorMessageWithLinkedDtcs } from '../../../../shared/models/error-messages/error-message-with-linked-dtcs';
import { ErrorMessageApiService } from '../../../services/api/error-message-api.service';
import { LinkedDtcsUpdateInfo } from '../../../../shared/models/error-message/linked-dtcs-update-info';
import { DtcLinkChangesInOtherVersionsTable } from '../../tables/dtc-link-changes-in-other-versions-table/dtc-link-changes-in-other-versions-table';
import { DtcLinkPropagationResult } from '../../../../shared/models/error-message/dtc-link-propagation-result';
import { DtcLinkPropagationInfo } from '../../../../shared/models/error-message/dtc-link-propagation-info';
import { DtcLinkState } from '../../../../shared/models/error-message/dtc-link-state';
import { AgentActionDirective } from '../../../directives/agent-action.directive';

@Component({
  selector: 'app-propagate-linked-dtcs-modal',
  imports: [DtcLinkChangesInOtherVersionsTable, AgentActionDirective],
  templateUrl: './propagate-linked-dtcs-modal.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './propagate-linked-dtcs-modal.scss',
})
export class PropagateLinkedDtcsModal extends BaseModal {
  @Output() public changesConfirmed = new EventEmitter<DtcLinkPropagationResult>();
  @Output() public notPropagateChanges = new EventEmitter<void>();

  protected linkedDtcsUpdateInfo?: LinkedDtcsUpdateInfo[];

  private readonly errorMessageApi = inject(ErrorMessageApiService);

  public override openModal(errorMessageWithLinkedDtcs?: ErrorMessageWithLinkedDtcs): void {
    this.linkedDtcsUpdateInfo = undefined;

    this.errorMessageApi.getLinkedDtcsUpdateInfo(errorMessageWithLinkedDtcs!).subscribe({
      next: (linkedDtcsUpdateInfo) => {
        if (this.shouldSkipPropagation(linkedDtcsUpdateInfo)) {
          this.notPropagateChanges.emit();
          return;
        }

        this.linkedDtcsUpdateInfo = linkedDtcsUpdateInfo;
        super.openModal();
      },
      error: (error) => {
        console.error('Error fetching linked DTCs update info:', error);
      },
    });
  }

  public override closeModal(): void {
    super.closeModal();
    this.linkedDtcsUpdateInfo = undefined;
  }

  protected confirmChanges(): void {
    const result = this.buildLinkPropagationResult();
    this.changesConfirmed.emit(result);
    this.closeModal();
  }

  private shouldSkipPropagation(linkedDtcsUpdateInfo: LinkedDtcsUpdateInfo[]): boolean {
    return linkedDtcsUpdateInfo.length === 0 || linkedDtcsUpdateInfo.every((updateInfo) => updateInfo.isEditedEntity);
  }

  private buildLinkPropagationResult(): DtcLinkPropagationResult {
    const siblingsToLink: DtcLinkPropagationInfo[] = [];
    const siblingsToUnlink: DtcLinkPropagationInfo[] = [];

    if (this.linkedDtcsUpdateInfo) {
      for (const updateInfo of this.linkedDtcsUpdateInfo) {
        if (updateInfo.isEditedEntity) {
          continue;
        }

        for (const dtcInfo of updateInfo.dtcLinkInfos) {
          if (!dtcInfo.selected) {
            continue;
          }
          const propagationInfo: DtcLinkPropagationInfo = {
            siblingErrorMessageId: updateInfo.errorMessageId,
            siblingDtcId: dtcInfo.dtcId,
          };
          if (dtcInfo.linkState === DtcLinkState.linkCanBeAdded) {
            siblingsToLink.push(propagationInfo);
          }

          if (dtcInfo.linkState === DtcLinkState.linkCanBeRemoved) {
            siblingsToUnlink.push(propagationInfo);
          }
        }
      }
    }
    return {
      dtcsToLink: siblingsToLink,
      dtcsToUnlink: siblingsToUnlink,
    } as DtcLinkPropagationResult;
  }
}
